"use client";

import { Session } from "next-auth";
import React, { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Home } from "lucide-react";
import { UserButton } from "@/components/auth/user-button";
import { CartButton } from "@/components/cart/cart-button";
import Link from "next/link";
import { useSidebarStore } from "@/hooks/use-sidebar-store";
import AuthWrapper from "@/components/auth/auth-wrapper";
import { Login, Register, ForgotPassword, OTPVerify } from "@/lib/validations";
import {
  LoginResponse,
  ForgotPasswordResponse,
  RegisterResponse,
  OTPVerifyResponse,
  ResendOTPResponse,
} from "@/types";
import MobileSearch from "./mobile-search";
import { SearchResult } from "@/store/search-store";
import { usePathname } from "next/navigation";

const mainNavItems = [{ icon: <Home size={24} />, href: "/" }];

interface MobileNavProps {
  session: Session | null;
  onLogin?: (values: Login) => Promise<LoginResponse>;
  onRegister?: (values: Register) => Promise<RegisterResponse>;
  onVerifyOTP?: (values: OTPVerify) => Promise<OTPVerifyResponse>;
  onForgotPassword?: (
    values: ForgotPassword
  ) => Promise<ForgotPasswordResponse>;
  onResendOTP?: (data: { email: string }) => Promise<ResendOTPResponse>;
  searchAction?: (query: string) => Promise<{
    success?: boolean;
    results?: SearchResult[];
  }>;
}

const MobileNav: React.FC<MobileNavProps> = ({
  session,
  onLogin,
  onRegister,
  onVerifyOTP,
  onForgotPassword,
  onResendOTP,
  searchAction,
}) => {
  const setOpen = useSidebarStore((state) => state.setOpen);
  const pathname = usePathname();
  const [isAccessDeniedPage, setIsAccessDeniedPage] = useState(false);

  // Check if we're on the access-denied page to disable cart/search
  useEffect(() => {
    setIsAccessDeniedPage(pathname === "/access-denied");
  }, [pathname]);

  // Extract company ID from session for search
  const userCompanyId = session?.user?.companyId || undefined;

  const handleNavClick = () => {
    setOpen(false);
  };

  return (
    <div className='bg-background'>
      <div className='border-t border-muted'>
        <nav className='flex justify-around items-center py-2 mb-2'>
          {mainNavItems.map((item, index) => (
            <Button
              key={index}
              variant='ghost'
              size='sm'
              className='h-12 w-12'
              asChild
              disabled={isAccessDeniedPage}
            >
              <Link
                href={isAccessDeniedPage ? "#" : item.href}
                onClick={
                  isAccessDeniedPage
                    ? (e) => e.preventDefault()
                    : handleNavClick
                }
                className={
                  isAccessDeniedPage
                    ? "cursor-not-allowed text-muted-foreground"
                    : ""
                }
              >
                {item.icon}
              </Link>
            </Button>
          ))}
          <MobileSearch
            searchAction={searchAction}
            companyId={userCompanyId}
            disabled={isAccessDeniedPage}
          />
          <CartButton isMobile={true} disabled={isAccessDeniedPage} />
          {session ? (
            <UserButton />
          ) : onLogin &&
            onRegister &&
            onVerifyOTP &&
            onForgotPassword &&
            onResendOTP ? (
            <AuthWrapper
              onLogin={onLogin}
              onRegister={onRegister}
              onVerifyOTP={onVerifyOTP}
              onForgotPassword={onForgotPassword}
              onResendOTP={onResendOTP}
            />
          ) : null}
        </nav>
      </div>
    </div>
  );
};

export default MobileNav;
